import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import APIClient from '../api/api';
import { User } from '../api/models';

interface AuthContextValue {
  user: User | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const navigate = useNavigate();
  // Stable ref so setUnauthorizedHandler doesn't need re-registration per render.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Bootstrap: attempt to restore a session using the stored refresh token.
  // If the server is unreachable but we still have a refresh token, retry
  // periodically instead of sending the user to the login page.
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout>;

    async function tryRestore() {
      const restored = await APIClient.tryRestoreSession();
      if (cancelled) return;
      if (restored) {
        setUser(restored);
        setStatus('authenticated');
      } else if (APIClient.getRefreshToken()) {
        // Server unreachable but tokens preserved — retry in a few seconds.
        retryTimer = setTimeout(tryRestore, 3000);
      } else {
        setUser(null);
        setStatus('unauthenticated');
      }
    }

    tryRestore();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, []);

  // Wire the 401-after-refresh-failed handler so axios can force a logout.
  useEffect(() => {
    APIClient.setUnauthorizedHandler(() => {
      setUser(null);
      setStatus('unauthenticated');
      navigateRef.current('/login', { replace: true });
    });
    return () => APIClient.setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const resp = await APIClient.login({ email, password });
    setUser(resp.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await APIClient.logout();
    setUser(null);
    setStatus('unauthenticated');
    navigate('/login', { replace: true });
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    try {
      const me = await APIClient.getCurrentUser();
      setUser(me);
    } catch {
      // ignore; the interceptor will handle hard failures
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      login,
      logout,
      refreshUser,
      isAdmin: user?.role === 'admin',
    }),
    [user, status, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
